import axios from 'axios';
import useTokenStore from '../stores/tokens';
import useModalStore from '../stores/modal';
import { API_BASE_URL } from '../const/constants';
import { toast } from 'react-toastify';
import toastConfig from '../const/toastConfig';

// Axios 인스턴스 생성
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 1000*30, // 30초
    headers: {
        'Content-Type': 'application/json',
    },
});

// 토큰 관리 유틸리티 함수들 (Zustand store 사용)
export const TokenManager = {
    getAccessToken: () => useTokenStore.getState().getAccessToken(),
    getRefreshToken: () => useTokenStore.getState().getRefreshToken(),
    setAccessToken: (token) => useTokenStore.getState().setAccessToken(token),
    setRefreshToken: (token) => useTokenStore.getState().setRefreshToken(token),
    setTokens: (accessToken, refreshToken) => useTokenStore.getState().setTokens(accessToken, refreshToken),
    clearTokens: () => useTokenStore.getState().clearTokens(),
    isTokenValid: () => useTokenStore.getState().isTokenValid(),
    setAuthenticated: (value) => useTokenStore.getState().setAuthenticated(value),
    isRefreshTokenValid: () => useTokenStore.getState().isRefreshTokenValid(),
    getUser: () => useTokenStore.getState().getUser(),
    isTokenExpired: (token) => {
        if (!token) return true;
        try {
            // tokens.js와 동일한 UTF-8 디코딩 방식 사용
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            const payload = JSON.parse(jsonPayload);
            return Date.now() >= payload.exp * 1000;
        } catch (error) {
            console.error('토큰 만료 확인 중 오류:', error);
            return true;
        }
    }
};

// 토큰 갱신 중복 요청 방지를 위한 플래그
let isRefreshing = false;
let failedQueue = [];

// 대기 중인 요청들을 처리하는 함수
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// 요청 인터셉터 - 모든 요청에 AccessToken 추가 및 선택된 조직 코드 헤더 추가
apiClient.interceptors.request.use(
    async (config) => {
        let accessToken = TokenManager.getAccessToken();
        // 로그인 과 같이 토큰 필요 없는 요청
        if(config.url.includes('/auth/login') || config.url.includes('/auth/refresh')){
            return config;
        }
        // 로그인이나 토큰 갱신 요청이 아닌 경우에만 토큰 추가
        config.headers.Authorization = `Bearer ${accessToken}`;

        // 선택된 조직 코드 헤더 추가
        const currentOrgCode = localStorage.getItem('currentOrganizationCode');
        if (currentOrgCode) {
            config.headers['X-Selected-OrgCode'] = currentOrgCode;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 응답 인터셉터 - 토큰 만료 시 자동 갱신
apiClient.interceptors.response.use(
    (response) => {return response;},
    async (error) => {
        const originalRequest = error.config;
        const status = error.response ? error.response.status : null;
        const errorData = error.response ? error.response.data : null;

        // 조직 권한 변경 / 조직 부재로 인한 403 Forbidden 에러 먼저 처리
        if (status === 403 && errorData && errorData.errorCode) {
            if (errorData.errorCode === 'NO_ORG_MEMBERSHIP') {
                toast.error(errorData.message || '소속된 조직이 없습니다. 로그아웃 합니다.', toastConfig);
                TokenManager.clearTokens();
                window.location.href = '/';
                return Promise.reject(error);
            }
            if (errorData.errorCode === 'ORG_ACCESS_REVOKED') {
                toast.error(errorData.message || '현재 조직에 대한 접근 권한이 없습니다. 조직을 다시 선택해 주세요.', toastConfig);
                try {
                    // 조직 목록 갱신
                    const orgsRes = await apiClient.get('/user/my-orgs');
                    const orgUserList = orgsRes.data || [];

                    if (orgUserList.length === 0) {
                        TokenManager.clearTokens();
                        window.location.href = '/';
                    } else {
                        // 현재 선택된 조직 초기화
                        useTokenStore.getState().setUser({
                            orgUserList: orgUserList,
                            currentOrganizationCode: null,
                            currentOrganizationName: null,
                            currentRole: null,
                            currentOrganizationApproved: false
                        });
                        localStorage.removeItem('currentOrganizationCode');
                        localStorage.removeItem('currentOrganizationName');
                        // 조직 선택 모달 열기
                        useModalStore.getState().openOrgSelectModal();
                    }
                } catch (e) {
                    console.error('Failed to sync org list on revoke:', e);
                    TokenManager.clearTokens();
                    window.location.href = '/';
                }
                return Promise.reject(error);
            }
        }

        // errorCode 없는 일반 403(정책 거부 등): 권한 없음 — 토큰 문제가 아니므로 리프레시하지 않고 메시지 표시 후 반려
        if (status === 403) {
            toast.error(errorData?.message || '접근 권한이 없습니다.', toastConfig);
            return Promise.reject(error);
        }

        // 401 에러이고 아직 재시도하지 않은 경우 (토큰 만료로 간주)
        if (status === 401 && !originalRequest._retry) {
            // 로그인이나 토큰 갱신 요청에서 401이 발생한 경우 패스
            if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
                return Promise.reject(error);
            }

            originalRequest._retry = true;
            const refreshToken = TokenManager.getRefreshToken();
            
            if (!refreshToken) {
                TokenManager.clearTokens();
                if (!window.location.pathname.includes('/login')) {
                    //alert('세션이 만료되었습니다. 다시 로그인해주세요.');
                    toast.error('세션이 만료되었습니다. 다시 로그인해주세요.', toastConfig);
                    window.location.href = '/';
                }
                return Promise.reject(new Error('No refresh token'));
            }
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axios(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            isRefreshing = true;

            try {
                const refreshToken = TokenManager.getRefreshToken();
                // RefreshToken으로 새로운 AccessToken 요청
                const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {
                    refreshToken: refreshToken
                }, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                const { accessToken: newAccessToken, refreshToken: newRefreshToken } = refreshResponse.data;
                TokenManager.setTokens(newAccessToken, newRefreshToken);

                // 대기 중인 요청들 처리
                processQueue(null, newAccessToken);
                // 원래 요청에 새 토큰으로 재시도
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return apiClient(originalRequest);

            } catch (error) {
                processQueue(error, null);
                TokenManager.clearTokens();
            } finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(error);
    }
);

// API 요청 함수들
export const interceptedApi = {
    // GET 요청
    get: async (endpoint, config = {}) => {
        try {
            const response = await apiClient.get(endpoint, config);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // POST 요청
    post: async (endpoint, data = null, config = {}) => {
        try {
            const response = await apiClient.post(endpoint, data, config);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // PUT 요청
    put: async (endpoint, data = null, config = {}) => {
        try {
            const response = await apiClient.put(endpoint, data, config);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // DELETE 요청
    delete: async (endpoint, config = {}) => {
        try {
            const response = await apiClient.delete(endpoint, config);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // PATCH 요청
    patch: async (endpoint, data = null, config = {}) => {
        try {
            const response = await apiClient.patch(endpoint, data, config);
            return response;
        } catch (error) {
            throw error;
        }
    }
};

// 인증 관련 함수들
export const authApi = {
    // 로그인
    login: async (credentials) => {
        try {
            const response = await apiClient.post('/auth/login', credentials);
            return response;
        } catch (error) {
            console.error('로그인 실패:', error);
            throw error;
        }
    },

    // 로그아웃
    logout: async () => {
        try {
            // 서버에 로그아웃 요청 (옵션)
            await apiClient.post('/auth/logout').catch(() => {
                // 로그아웃 요청이 실패해도 로컬 토큰은 삭제
            });
        } catch (error) {
            console.error('서버 로그아웃 요청 실패:', error);
        } finally {
            TokenManager.clearTokens();
            console.log('로그아웃 완료');
            //window.location.href = '/login';
        }
    },

    // 토큰 수동 갱신
    refreshTokens: async () => {
        try {
            const refreshToken = TokenManager.getRefreshToken();
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }

            const response = await apiClient.post('/auth/refresh', {
                refreshToken: refreshToken
            });

            const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;
            
            // 새 토큰들 저장
            TokenManager.setTokens(newAccessToken, newRefreshToken);
            TokenManager.setAuthenticated(true);
            return response;
        } catch (error) {
            console.error('토큰 갱신 실패:', error);
            TokenManager.clearTokens();
            throw error;
        }
    },

    // 현재 사용자 정보 조회
    getCurrentUser: async () => {
        try {
            const response = await apiClient.get('/auth/me');
            return response;
        } catch (error) {
            console.error('사용자 정보 조회 실패:', error);
            throw error;
        }
    },

    // 현재 사용자의 조직 목록 조회
    getCurrentUserOrgs: async () => {
        try {
            const response = await apiClient.get('/user/my-orgs');
            return response;
        } catch (error) {
            console.error('조직 목록 조회 실패:', error);
            throw error;
        }
    }
};

// 기존 ApiRequest.js와의 호환성을 위한 래퍼 함수들
export const compatApi = {
    get: async (endpoint, headers = {}) => {
        try {
            const response = await interceptedApi.get(endpoint, headers );
            //console.log('compatApi GET response:', response);
            return {
                ok: response.status >= 200 && response.status < 300,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                json: () => Promise.resolve(response.data),
                text: () => Promise.resolve(typeof response.data === 'string' ? response.data : JSON.stringify(response.data)),
                data: response.data
            };
        } catch (error) {
            console.error('compatApi GET error:', error);
            throw error;
        }
    },

    post: async (endpoint, body, headers = {}) => {
        try {
            const response = await interceptedApi.post(endpoint, body, { headers });
            return {
                ok: response.status >= 200 && response.status < 300,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                json: () => Promise.resolve(response.data),
                text: () => Promise.resolve(typeof response.data === 'string' ? response.data : JSON.stringify(response.data)),
                data: response.data
            };
        } catch (error) {
            console.error('compatApi POST error:', error);
            throw error;
        }
    },

    put: async (endpoint, body, headers = {}) => {
        try {
            const response = await interceptedApi.put(endpoint, body, { headers });
            return {
                ok: response.status >= 200 && response.status < 300,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                json: () => Promise.resolve(response.data),
                text: () => Promise.resolve(typeof response.data === 'string' ? response.data : JSON.stringify(response.data)),
                data: response.data
            };
        } catch (error) {
            console.error('compatApi PUT error:', error);
            throw error;
        }
    },

    del: async (endpoint, body, headers = {}) => {
        try {
            const response = await interceptedApi.delete(endpoint, { data: body, headers });
            return {
                ok: response.status >= 200 && response.status < 300,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                json: () => Promise.resolve(response.data),
                text: () => Promise.resolve(typeof response.data === 'string' ? response.data : JSON.stringify(response.data)),
                data: response.data
            };
        } catch (error) {
            console.error('compatApi DELETE error:', error);
            throw error;
        }
    },

    upload: async (endpoint, formData, headers = {}) => {
        try {
            const response = await interceptedApi.post(endpoint, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    ...headers
                }
            });
            return {
                ok: response.status >= 200 && response.status < 300,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                json: () => Promise.resolve(response.data),
                text: () => Promise.resolve(typeof response.data === 'string' ? response.data : JSON.stringify(response.data)),
                data: response.data
            };
        } catch (error) {
            console.error('compatApi UPLOAD error:', error);
            throw error;
        }
    },

    postFile: async (endpoint, body, config = {}) => {
            try {
                const response = await interceptedApi.post(endpoint, body, config);
                return {
                    ok: response.status >= 200 && response.status < 300,
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                    json: () => Promise.resolve(response.data),
                    text: () => Promise.resolve(typeof response.data === 'string' ? response.data : JSON.stringify(response.data)),
                    data: response.data
                };
            } catch (error) {
                console.error('compatApi POST error:', error);
                throw error;
            }
        },

};

const intercepterExports = {
    interceptedApi,
    authApi,
    compatApi,
    TokenManager,
    apiClient
};
export default intercepterExports;
